import type { QuoteRepository } from '../../application/QuoteRepository'
import type { QuoteDataSource, QuoteStats } from '../../domain/quote'
import { quotesFixtures } from './quotesFixtures'

export function createFixtureQuoteRepository(source: QuoteDataSource = 'fixtures'): QuoteRepository {
  const records = [...quotesFixtures]
  return {
    list(filters) {
      const search = filters.search?.trim().toLocaleLowerCase('fr-CA')
      const filtered = records.filter((quote) => {
        if (filters.status && quote.status !== filters.status) return false
        if (!search) return true
        return [quote.number, quote.notes, quote.party.displayName, quote.party.reference]
          .filter(Boolean)
          .some((value) => value?.toLocaleLowerCase('fr-CA').includes(search))
      })
      const start = (filters.page - 1) * filters.pageSize
      const count = (status: (typeof quotesFixtures)[number]['status']) =>
        records.filter((quote) => quote.status === status).length
      const stats: QuoteStats = {
        total: records.length,
        draft: count('DRAFT'),
        ready: count('READY'),
        sent: count('SENT'),
        accepted: count('ACCEPTED'),
        rejected: count('REJECTED'),
        expired: count('EXPIRED'),
      }
      return Promise.resolve({
        items: filtered.slice(start, start + filters.pageSize),
        page: filters.page,
        pageSize: filters.pageSize,
        total: filtered.length,
        stats,
        meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 },
      })
    },
    getByReference(reference) {
      const quote = records.find((item) => item.id === reference || item.number === reference)
      return Promise.resolve(
        quote
          ? { quote, items: [], meta: { source, retrievedAt: new Date().toISOString(), anomalies: 0 } }
          : null,
      )
    },
    createDraft(command) {
      const existing = records.find((quote) => quote.id === command.idempotencyKey)
      if (existing) {
        return Promise.resolve({
          quoteId: existing.id,
          number: existing.number,
          status: 'DRAFT',
          version: 1,
          createdAt: existing.createdAt,
        })
      }
      const createdAt = new Date().toISOString()
      const number = `SOU-DEMO-${String(records.length + 1).padStart(3, '0')}`
      records.unshift({
        id: command.idempotencyKey,
        number,
        status: 'DRAFT',
        subtotalCents: 0,
        taxTotalCents: 0,
        totalCents: 0,
        version: 1,
        ...(command.clientId ? { clientId: command.clientId } : {}),
        ...(command.leadId ? { leadId: command.leadId } : {}),
        party: {
          kind: command.leadId ? 'LEAD' : 'CLIENT',
          displayName: command.recipientDisplayName,
        },
        ...(command.validUntil ? { validUntil: command.validUntil } : {}),
        ...(command.notes ? { notes: command.notes } : {}),
        createdAt,
        updatedAt: createdAt,
      })
      return Promise.resolve({
        quoteId: command.idempotencyKey,
        number,
        status: 'DRAFT',
        version: 1,
        createdAt,
      })
    },
    replaceDraftItems() {
      return Promise.reject(new Error('QUOTE_TAX_PROFILE_COMPLETE_REQUIRED'))
    },
    prepareDraft() {
      return Promise.reject(new Error('QUOTE_PREPARATION_SUPABASE_REQUIRED'))
    },
    sendPrepared() {
      return Promise.reject(new Error('QUOTE_DELIVERY_SUPABASE_REQUIRED'))
    },
    acceptSent() {
      return Promise.reject(new Error('QUOTE_ACCEPTANCE_SUPABASE_REQUIRED'))
    },
    setCompetitor() {
      return Promise.reject(new Error('QUOTE_COMPETITOR_SUPABASE_REQUIRED'))
    },
  }
}
